import { OutlineElement } from '../../../base/outline-element/outline-element';
import { CSSResultGroup, html, TemplateResult } from 'lit';
import {
  customElement,
  property,
  state,
  query,
  queryAll,
} from 'lit/decorators.js';

import componentStyles from './ghs-navigation.css.lit';
import '../../ghs-icon/ghs-icon';

import { SlotController } from '../../../controllers/slot-controller';

import {
  screenSizeMap,
  ScreenSizeController,
} from '../../../controllers/screen-size-controller';

/**
 * The Subnav Component
 *
 * @element ghs-navigation
 * @slot accepts nested ul li elements to create the subnav component
 */
@customElement('ghs-navigation')
export class GhsNavigation extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  slots = new SlotController(this, true);

  // eslint-disable-next-line
  // @ts-ignore
  private _screenSizeController = new ScreenSizeController(this);

  /**
   * Whether or not this is a subnav component.
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-subnav' })
  isSubnav = false;

  /**
   * Whether or not this is a subnav variant.
   */
  @property({ type: Boolean, reflect: true, attribute: 'subnav-variant' })
  subnavVariant = false;

  /**
   * Whether or not this is a utility menu. Fits in the header to provide dropdown functionality, if required.
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-utility-menu' })
  isUtilityMenu = false;

  /**
   * Whether or not this is a secondary menu.
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-secondary' })
  isSecondary = false;

  @property({ type: Boolean, reflect: true, attribute: 'has-navigation' })
  hasNavigation: boolean;

  @state()
  isMobile = window.innerWidth < (screenSizeMap.get('lg') as number);

  topLevels: NodeListOf<Element>;

  @query('.button--prev')
  prevBtn: HTMLButtonElement;

  @query('.button--next')
  nextBtn: HTMLButtonElement;

  @query('.navigation')
  navigationEl: HTMLLIElement;

  @query('nav')
  navEl: HTMLLIElement;

  @query('.dropdown-trigger.show')
  navShow: HTMLLIElement;

  @query('nav > ul')
  navWrapper: HTMLLIElement;

  @queryAll('nav > ul > li')
  navMainItems: HTMLLIElement[];

  navPaddingRight = 48;
  translateX = 0;

  nextItemPosition = 0;
  prevItemPosition = 0;

  lastItemPosition: number;

  updated(): void {
    if (this.isSubnav) {
      this._resetSubNavigationVariables();
      this._resetSubNavigationElements();
    }
  }

  _resetSubNavigationElements() {
    this._disabledButton(this.prevBtn);
    this.navigationEl.classList.add('has-navigation--start');

    if (this.navWrapper) {
      this.navWrapper.style.transform = `translateX(0px)`;
    }
  }

  _resetSubNavigationVariables() {
    this.translateX = 0;
    this.nextItemPosition = 0;
    this.prevItemPosition = 0;
  }

  _findNextItemPosition(): void {
    const items = [...this.navMainItems];
    const navBoundingRight = this.navEl.getBoundingClientRect().right;

    const nextIndex = items.findIndex(item => {
      const itemBoundingRight = item.getBoundingClientRect().right;

      return itemBoundingRight >= navBoundingRight;
    });

    this.nextItemPosition = nextIndex + 1;
  }

  _findPrevItemPosition() {
    const reversedItems = [...this.navMainItems].reverse();
    const navBoundingLeft = this.navEl.getBoundingClientRect().left;

    const prevReverseIndex = reversedItems.findIndex(item => {
      const itemBoundingLeft = item.getBoundingClientRect().left;

      return itemBoundingLeft <= navBoundingLeft;
    });

    this.prevItemPosition = this.lastItemPosition - prevReverseIndex;
  }

  _isLastPosition() {
    return this.nextItemPosition == this.lastItemPosition;
  }

  _isFirstPosition() {
    return this.prevItemPosition == 1;
  }

  _scrollLeftNav(prevItem: HTMLElement) {
    const prevItemBoundingLeft = prevItem.getBoundingClientRect().left;
    const navBoundingLeft = this.navEl.getBoundingClientRect().left;
    const navPadding = this._isFirstPosition() ? 0 : this.navPaddingRight;
    const translateR = navBoundingLeft - prevItemBoundingLeft + navPadding;

    this.translateX = this.translateX - translateR;

    if (this.navWrapper) {
      this.navWrapper.style.transform = `translateX(-${this.translateX}px)`;
    }
  }

  _scrollRightNav(nextItem: HTMLElement) {
    const nextItemBoundingRight = nextItem.getBoundingClientRect().right;
    const navBoundingRight = this.navEl.getBoundingClientRect().right;
    const navPadding = this._isLastPosition() ? 0 : this.navPaddingRight;

    this.translateX =
      this.translateX + nextItemBoundingRight - navBoundingRight + navPadding;

    if (this.navWrapper) {
      this.navWrapper.style.transform = `translateX(-${this.translateX}px)`;
    }
  }

  _enableButton(button: HTMLButtonElement) {
    button?.removeAttribute('disabled');
  }

  _disabledButton(button: HTMLButtonElement) {
    button?.setAttribute('disabled', 'disabled');
  }

  _handleNextBtnClick = (slotNav: HTMLElement | null) => () => {
    this._enableButton(this.prevBtn);
    this.navigationEl.classList.remove('has-navigation--start');

    if (slotNav) {
      if (!this.nextItemPosition) {
        this._findNextItemPosition();
      }

      const selector = `nav > ul > li:nth-child(${this.nextItemPosition})`;
      const nextItem: HTMLElement | null = slotNav.querySelector(selector);

      if (this._isLastPosition()) {
        this._disabledButton(this.nextBtn);
        this.navigationEl.classList.add('has-navigation--last');
      }

      if (nextItem) {
        this._scrollRightNav(nextItem);
        this.nextItemPosition++;
      }
    }
  };

  _handlePrevBtnClick = (slotNav: HTMLElement | null) => () => {
    this._enableButton(this.nextBtn);
    this.navigationEl.classList.remove('has-navigation--last');

    if (slotNav) {
      if (slotNav) {
        if (!this.prevItemPosition) {
          this._findPrevItemPosition();
        }

        const selector = `nav > ul > li:nth-child(${this.prevItemPosition})`;
        const prevItem: HTMLElement | null = slotNav?.querySelector(selector);

        if (this._isFirstPosition()) {
          this._disabledButton(this.prevBtn);
          this.navigationEl.classList.add('has-navigation--start');
        }

        if (prevItem) {
          this._scrollLeftNav(prevItem);
          this.prevItemPosition--;
          this.nextItemPosition--;
        }
      }
    }
  };

  createIcon(name: string): HTMLElement {
    const icon = document.createElement('ghs-icon');

    icon.setAttribute('name', name);
    icon.setAttribute('size', '20px');
    icon.setAttribute('site', this.site);

    return icon;
  }

  setNavButtons(navSlot: HTMLElement) {
    if (navSlot) {
      // Add next navigation button.
      const nextBtn = document.createElement('button');
      nextBtn.setAttribute('class', 'button button--next');
      nextBtn.appendChild(this.createIcon('chevron-right'));
      nextBtn.addEventListener('click', this._handleNextBtnClick(navSlot));

      // Add prev navigation button.
      const prevBtn = document.createElement('button');
      prevBtn.setAttribute('class', 'button button--prev');
      prevBtn.setAttribute('disabled', 'true');
      prevBtn.appendChild(this.createIcon('chevron-left'));
      prevBtn.addEventListener('click', this._handlePrevBtnClick(navSlot));

      // Attached navigation button.
      navSlot?.append(nextBtn);
      navSlot?.prepend(prevBtn);
    }
  }

  _getNavWith(): number {
    const screenSize = this._screenSizeController.screenSize;
    let navWidth;

    switch (screenSize) {
      case 'xl':
        navWidth = 1152;
        break;

      case 'xxl':
      case 'xxxl':
        navWidth = 1280;
        break;

      case 'lg':
      default:
        navWidth = 896;
    }

    return navWidth;
  }

  _needsNavigation(children: NodeListOf<Element>): boolean {
    const accItemsWidth = [...children].reduce((acc, child) => {
      acc = acc + child?.getBoundingClientRect().width;
      return acc;
    }, 0);

    const navWrapperWidth = accItemsWidth + (children.length - 1) * 24;
    const minNavWith = this._getNavWith();

    return navWrapperWidth >= minNavWith;
  }

  initNavigationSettings(navSlot: HTMLElement) {
    // Look for last item position.
    const children = navSlot.querySelectorAll('nav > ul > li');
    this.lastItemPosition = children.length;

    if (this._needsNavigation(children)) {
      this.setNavButtons(navSlot);

      this.navigationEl.classList.add('has-navigation');
      this.navigationEl.classList.add('has-navigation--start');
    }
  }

  firstUpdated(): void {
    super.firstUpdated();
    // This timeout is important for slot controller to finish before these things run.
    setTimeout(() => {
      // @ts-expect-error shadowRoot makes this think it is not an element because reasons.
      this.topLevels = this.shadowRoot.querySelectorAll('.menu-level--0');
      this.setIds();
      this.setTriggerAttributes();
      this.addListeners();

      if (this.isSubnav) {
        const navSlot: HTMLElement | null | undefined =
          this.shadowRoot?.querySelector('[cloned-slot-name="navigation"]');

        if (navSlot) {
          this.initNavigationSettings(navSlot);
        }
      }
    }, 0);
  }

  /**
   * Active dropdown using the focused ID.
   */
  activeDropdownEl(el: Element | null): void {
    if (el) {
      const keyboardTrigger = el.querySelector('.dropdown-trigger');

      el.setAttribute('is-dropdown-open', 'true');
      keyboardTrigger?.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Inactive dropdown using the focused ID.
   */
  inactiveDropdownEl(el: Element | null): void {
    if (el) {
      const keyboardTrigger = el.querySelector('.dropdown-trigger');

      el.removeAttribute('is-dropdown-open');
      keyboardTrigger?.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Inactive dropdown using the focused ID.
   */
  handleClickDropdownEl(el: Element | null): void {
    if (el) {
      const isOpen = el.getAttribute('is-dropdown-open');

      [...this.topLevels].forEach(item => {
        if (item === el) {
          isOpen ? this.inactiveDropdownEl(el) : this.activeDropdownEl(el);
        } else {
          this.inactiveDropdownEl(item);
        }
      });
    }
  }

  /**
   * Sets a unique ID on menu list items.
   */
  setIds(): void {
    const mainItems = [...this.topLevels.values()];
    const randomNum = Math.floor(Math.random() * 10000).toString();

    mainItems.map((el, i) => {
      el.setAttribute('id', `${randomNum}-${i}-main`);
    });
  }

  /**
   * Show or hide the subnav on label click.
   */
  showSubNav(): void {
    this.navEl?.classList.toggle('hide');
    this.navShow?.classList.toggle('open');
  }

  /**
   * Listens for mouse and keyboard events.
   */
  addListeners(): void {
    const mainItems = [...this.topLevels.values()];

    const subNavOpen = this.shadowRoot?.querySelector('.dropdown-trigger.show');

    subNavOpen?.addEventListener('click', () => this.showSubNav());

    mainItems.map(el => {
      const keyboardTrigger = el.querySelector('.dropdown-trigger');

      if (this.isMobile) {
        el.addEventListener('click', () => this.handleClickDropdownEl(el));
      } else {
        el.addEventListener('mouseenter', () => this.activeDropdownEl(el));
        el.addEventListener('mouseleave', () => this.inactiveDropdownEl(el));
      }

      /* eslint-disable  @typescript-eslint/no-explicit-any */
      keyboardTrigger?.addEventListener('keydown', (ev: any) => {
        const { code } = ev;

        if (code === 'Enter' || code === 'Space') {
          this.activeDropdownEl(el);
        }

        if (!this.isMobile && code === 'Escape') {
          this.inactiveDropdownEl(el);
        }
      });
    });
  }

  isSecondaryMenu(): string {
    return this.isSecondary ? 'secondary-menu' : '';
  }

  isSubnavVariant(): string {
    return this.subnavVariant ? 'subnav-variant' : '';
  }

  /**
   * Sets aria and ID attributes on dropdown trigger and dropdown ul.
   */
  setTriggerAttributes(): void {
    const childrenLevels = [...this.querySelectorAll('.has-children').values()];
    const randomNum = Math.floor(Math.random() * 10000).toString();

    childrenLevels.map((el, i) => {
      const dropdown = el.getElementsByTagName('ul')[0];
      const uniqueId = `${randomNum}-${i}-dropdown`;
      const trigger = el.querySelector('.dropdown-trigger');

      if (dropdown === null || trigger === null) {
        return;
      }
      dropdown?.setAttribute('id', uniqueId);
      trigger.setAttribute('aria-controls', uniqueId);
    });
  }

  render(): TemplateResult {
    return html`
      <div
        class="navigation ${this.isSecondaryMenu()} ${this.isSubnavVariant()}"
      >
        <slot name="subnav-label"></slot>
        <slot name="navigation"></slot>
      </div>
    `;
  }
}
